import { CalculatorInputs, initialCalculatorInputs } from './CalculatorInputs';
import {
    Validation,
    validateStep1,
    ValidationSuccess,
    validateUpToStep2,
    validateUpToStep3,
    validateUpToStep4
} from './CalculatorInputsValidation';
import { CalculatorConfig, } from "./CalculatorConfig";
import { Option, some, none } from 'fp-ts/lib/Option';


export class Step {

    readonly name: string;
    readonly validate: (inputs: CalculatorInputs, config: CalculatorConfig) => Validation;

    constructor(name: string,
        validate: (inputs: CalculatorInputs, config: CalculatorConfig) => Validation) {
        this.name = name;
        this.validate = validate;
    }

    equals(step: Step): boolean {
        return this.name === step.name;
    }
}

export class CalculatorSteps {

    static readonly STEP_1_ENTER_PREMIUM_CHOOSE_TERM = 0;
    static readonly STEP_2_SELECT_STRATEGY_TYPES = 1;
    static readonly STEP_3_CHOOSE_BUFFERS = 2;
    static readonly STEP_4_CHOOSE_INDICES_ENTER_ALLOCATIONS = 3;

    readonly steps: Array<Step>;
    readonly stepIndex: number;

    readonly inputs: CalculatorInputs;
    readonly config: CalculatorConfig;
    readonly validation: Validation;
    readonly shouldRunValidations: boolean;
    readonly firstValidationError: Option<string>;

    constructor(steps: Array<Step>,
        index: number,
        inputs: CalculatorInputs,
        config: CalculatorConfig,
        validation: Validation,
        shouldRunValidations: boolean,
        firstValidationError: Option<string>) {
        this.steps = steps;
        this.stepIndex = index;

        if (this.steps.length === 0) {
            throw new RangeError('CalculatorSteps must have at least one step');
        }
        if (this.stepIndex < 0 || this.stepIndex >= this.steps.length) {
            throw new RangeError(`stepIndex '${this.stepIndex}' does not refer to an element of steps (length ${this.steps.length})`)
        }

        this.inputs = inputs;
        this.config = config;
        this.validation = validation;
        this.shouldRunValidations = shouldRunValidations;
        this.firstValidationError = firstValidationError;
    }

    atStep(stepIndex: number): CalculatorSteps {
        return new CalculatorSteps(this.steps,
            stepIndex,
            this.inputs, this.config, this.validation, this.shouldRunValidations, this.firstValidationError);
    }

    withInputs(inputs: CalculatorInputs): CalculatorSteps {
        return new CalculatorSteps(this.steps, this.stepIndex, inputs, this.config, this.validation, this.shouldRunValidations, this.firstValidationError);
    }

    getStep(): Step {

        return this.steps[this.stepIndex];
    }

    withStepIndex(index: number): CalculatorSteps {
        if (index < 0 || index >= this.steps.length) {
            throw new RangeError(`index ${index} does not refer to a step (step count ${this.steps.length})`);
        }
        return new CalculatorSteps(this.steps, index, this.inputs, this.config, this.validation, this.shouldRunValidations, this.firstValidationError);
    }

    validatedAtCurrentStep(inputs: CalculatorInputs): CalculatorSteps {
        const newVal = this.getStep().validate(inputs, this.config);
        return new CalculatorSteps(this.steps, this.stepIndex, inputs, this.config, newVal, this.shouldRunValidations, this.firstValidationError);
    }

    withValidation(v: Validation): CalculatorSteps {
        return new CalculatorSteps(this.steps, this.stepIndex, this.inputs, this.config, v, this.shouldRunValidations, this.firstValidationError);
    }

    withConfig(config: CalculatorConfig): CalculatorSteps {
        return new CalculatorSteps(this.steps, this.stepIndex, this.inputs, config, this.validation, this.shouldRunValidations, this.firstValidationError);
    }

    withRunValidationsOn(): CalculatorSteps {
        return new CalculatorSteps(this.steps, this.stepIndex, this.inputs, this.config, this.validation, true, this.firstValidationError);
    }
    withRunValidationsOff(): CalculatorSteps {
        return new CalculatorSteps(this.steps, this.stepIndex, this.inputs, this.config, this.validation, false, this.firstValidationError);
    }

    withFirstValidationError(firstValidationError: string): CalculatorSteps {
        return new CalculatorSteps(this.steps, this.stepIndex, this.inputs, this.config, this.validation, this.shouldRunValidations, some(firstValidationError));
    }
    withoutFirstValidationError(): CalculatorSteps {
        return new CalculatorSteps(this.steps, this.stepIndex, this.inputs, this.config, this.validation, this.shouldRunValidations, none);
    }
}

export function createIntialCalculatorSteps(config: CalculatorConfig) {

    const step1 = new Step("Step 1", validateStep1);
    const step2 = new Step("Step 2", validateUpToStep2);
    const step3 = new Step("Step 3", validateUpToStep3);
    const step4 = new Step("Step 4", validateUpToStep4);

    return new CalculatorSteps([step1, step2, step3, step4], 0,
        initialCalculatorInputs,
        config,
        new ValidationSuccess(),
        false,
        none);
}
